import { useCallback } from 'react';

import { VisitPatient } from '@dreryk/egabinet-visit-frontend-api';
import { EducationalMaterial, EducationalMaterialOperationType } from '@dreryk/egabinet-knowledge-base-frontend-api';

import { useOpenInNewTab } from '@dreryk/react-common';
import {
   dispatchSnackbarNegativeEvent,
   DownloadIcon,
   EmailEmptyIcon,
   IconSize,
   IconWrapper,
   Link,
   LinkTarget,
   OpenInNewIcon,
} from '@dreryk/react-components';

import { VisitId } from '~libs.shared.visit';

import { educationalMaterialsTranslationKeys } from '../../translations';
import { EducationalMaterialShareShowModalInterceptor } from '../../share/EducationalMaterialShareShowModalInterceptor';
import { EducationalMaterialRemoveHandler } from '../EducationalMaterialsTable.types';
import { useEducationalMaterialVisitOperationRegisterCommand } from '../EducationalMaterialsTable.hooks';

type OptionsCellProps = {
   contentUrl: string;
   visitId: VisitId;
   patientEmail?: VisitPatient['emailAddress'];
   educationalMaterials: EducationalMaterial[];
   onEducationalMaterialRemoved?: EducationalMaterialRemoveHandler;
};

export const OptionsCell = (props: OptionsCellProps): JSX.Element => {
   const { openInNewTab } = useOpenInNewTab();

   const { executeAsync: registerEducationalMaterialOperation } = useEducationalMaterialVisitOperationRegisterCommand(
      props.educationalMaterials
   );
   const registerOperation = useCallback(
      (operationType: EducationalMaterialOperationType) => {
         return async () => registerEducationalMaterialOperation(operationType);
      },
      [registerEducationalMaterialOperation]
   );
   const handleOpenInNewTab = useCallback(
      async () =>
         openInNewTab(props.contentUrl)
            .then(registerOperation('VIEW'))
            .catch(() => dispatchSnackbarNegativeEvent(educationalMaterialsTranslationKeys.openError)),
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [props.contentUrl, openInNewTab]
   );

   return (
      <>
         <Link
            onClick={handleOpenInNewTab}
            icon={
               <IconWrapper testId={'openContent'} size={IconSize.SMALL}>
                  <OpenInNewIcon />
               </IconWrapper>
            }
            testId={'openContent'}
         />
         <Link
            href={props.contentUrl}
            target={LinkTarget.BLANK}
            onClick={registerOperation('DOWNLOAD')}
            icon={
               <IconWrapper testId={'downloadContent'} size={IconSize.SMALL}>
                  <DownloadIcon testId={'downloadContent'} />
               </IconWrapper>
            }
            testId={'downloadContent'}
         />
         <EducationalMaterialShareShowModalInterceptor
            visitId={props.visitId}
            patientEmail={props.patientEmail}
            educationalMaterials={props.educationalMaterials}
            onEducationalMaterialRemoved={props.onEducationalMaterialRemoved}
            onEducationalMaterialsShareSuccess={registerOperation('SENT')}
         >
            <Link
               icon={
                  <IconWrapper testId={'shareContent'} size={IconSize.SMALL}>
                     <EmailEmptyIcon />
                  </IconWrapper>
               }
               testId={'shareContent'}
            />
         </EducationalMaterialShareShowModalInterceptor>
      </>
   );
};
